<script setup>
  import * as Sentry from '@sentry/vue'

  import { nextTick, onMounted, watch } from 'vue'

  import { useAuthStore } from '@/features/auth/useAuthStore.js'
  import { isInIframe } from '@/features/embed/isInIframe.js'
  import { initProductFruits } from '@/features/productFruits/productFruits.js'
  import GlobalConfirmModal from '@/features/ui/components/GlobalConfirmModal.vue'
  import ModalDialog from '@/features/ui/components/modalDialog/ModalDialog.vue'
  import { useNetworkStatus } from '@/features/ui/hooks/useNetworkStatus.js'

  const authStore = useAuthStore()
  const { isOnline, showOfflineModal, hideOfflineModal } = useNetworkStatus()

  watch(
    [() => authStore.isLoggedIn, () => authStore.myDetails],
    async ([isLoggedIn, details]) => {
      if (!isLoggedIn || !details) {
        return
      }

      await nextTick()

      initProductFruits({
        email: details.email,
        firstname: details.name,
        lastname: details.surname,
        appUserId: details.appUserId,
        profileId: details.profileId,
        role: details.typeString,
      })
    },
    { immediate: true },
  )

  onMounted(() => {
    Sentry.setTag('isInIframe', isInIframe())
  })
</script>

<template>
  <Toast />
  <ConfirmPopup />
  <GlobalConfirmModal />
  <RouterView />

  <!-- Network Connection Warning Modal -->
  <ModalDialog
    v-model:visible="showOfflineModal"
    :modal="true"
    :closable="false"
    :dismissable-mask="false"
    :severity="isOnline ? 'success' : 'error'"
    :header="isOnline ? 'Connection Restored' : 'Connection Lost'"
    :style="{ width: '400px' }"
  >
    <div class="network-warning-content">
      <div class="warning-icon">
        <i
          class="pi pi-wifi"
          style="font-size: 3rem"
          :class="isOnline ? 'status-online pi-check-circle' : 'status-offline pi-times-circle'"
        ></i>
      </div>
      <p class="warning-message">
        <template v-if="!isOnline">
          Your internet connection has been lost. Please check your network settings or try again
          later.
        </template>

        <template v-else> You are back online! You can continue using the application. </template>
      </p>
      <div class="connection-status">
        <span v-if="!isOnline" class="status-offline">
          <i class="pi pi-times-circle"></i> Offline
        </span>
        <span v-else class="status-online"> <i class="pi pi-check-circle"></i> Connected </span>
      </div>
    </div>

    <template #footer>
      <Button
        v-if="isOnline"
        label="Continue"
        severity="success"
        autofocus
        fluid
        aria-label="Continue"
        @click="hideOfflineModal"
      />
    </template>
  </ModalDialog>
</template>

<style>
  .network-warning-content {
    text-align: center;
    padding: 1rem 0;
  }

  .warning-icon {
    margin-bottom: 1rem;
  }

  .warning-message {
    font-size: 1.1rem;
    color: #333;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  .connection-status {
    font-weight: 600;
    font-size: 1rem;
  }

  .status-offline {
    color: #f44336;
  }

  .status-online {
    color: #4caf50;
  }

  .status-offline i,
  .status-online i {
    margin-right: 0.5rem;
  }
</style>
